// src/screens/CheckUp.tsx
// Source health dashboard.
//   Section 1 — SCRAPERS: per-source cycle tracking (is the scraper advancing?)
//   Section 2 — BOFA WORKERS: is each BofA worker processing when it has work?
// Auto-refreshes every 60s. Click row to see failures.

import { useEffect, useState, useCallback, Fragment } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiFetch } from '../helpers';

type ScraperStatus = 'healthy' | 'stopped' | 'quota_paused' | 'no_data' | 'disabled';
type BofaStatus = 'healthy' | 'stalled' | 'proxy_broken' | 'success_dropped';

interface ScraperRow {
  source: string;
  mode: string;
  status: ScraperStatus;
  cycle: number | null;
  detail: string;
  heartbeatMinutesAgo: number | null;
  thresholdMin: number | null;
}

interface BofaRow {
  source: string;
  queueCount: number;
  lastProcessedMinutesAgo: number | null;
  processedLast1h: number;
  errors24h: { noData: number; proxyFailed: number; other: number; total: number };
  successCount24h: number;
  attemptedTotal24h: number;
  successPct: number | null;
  proxyPct: number;
  successRedPct: number;
  proxyRedPct: number;
  idleThresholdMin: number;
  note: string | null;
  status: BofaStatus;
}

interface GateInfo {
  killed: boolean;
  dailyUsed: number | null;
  dailyLimit: number | null;
}

interface CheckupResponse {
  success: boolean;
  generatedAt: string;
  scrapers: ScraperRow[];
  bofaWorkers: BofaRow[];
  rapidApiGate: GateInfo | null;
}

interface FailureRow {
  _id: string;
  fullAddress: string;
  amvError: string;
  dealStage: string;
  updatedAt: string;
}

function formatAge(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)}h ago`;
  return `${(minutes / 1440).toFixed(1)}d ago`;
}

function scraperChip(status: ScraperStatus) {
  switch (status) {
    case 'healthy':       return <Chip label="Healthy" color="success" size="small" />;
    case 'stopped':       return <Chip label="Stopped" color="error" size="small" />;
    case 'quota_paused':  return <Chip label="Quota paused" color="warning" size="small" />;
    case 'no_data':       return <Chip label="No signal" color="default" size="small" />;
    case 'disabled':      return <Chip label="Disabled" color="default" size="small" />;
    default:              return <Chip label={status} size="small" />;
  }
}

function bofaChip(status: BofaStatus) {
  switch (status) {
    case 'healthy':          return <Chip label="Healthy" color="success" size="small" />;
    case 'stalled':          return <Chip label="Stalled" color="error" size="small" />;
    case 'proxy_broken':     return <Chip label="Proxy broken" color="error" size="small" />;
    case 'success_dropped':  return <Chip label="Success dropped" color="error" size="small" />;
    default:                 return <Chip label={status} size="small" />;
  }
}

export default function CheckUp() {
  const [data, setData] = useState<CheckupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [failures, setFailures] = useState<Record<string, FailureRow[]>>({});
  const [failuresLoading, setFailuresLoading] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const load = useCallback(async () => {
    try {
      setErr(null);
      const res = await apiFetch('/api/checkup');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'failed');
      setData(json);
      setLastRefresh(new Date());
    } catch (e: any) {
      setErr(e.message || 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFailures = useCallback(async (source: string) => {
    setFailuresLoading(source);
    try {
      const res = await apiFetch(`/api/checkup/${source}`);
      const json = await res.json();
      if (json.success) {
        setFailures(prev => ({ ...prev, [source]: json.failures || [] }));
      }
    } catch { /* ignore */ }
    finally { setFailuresLoading(null); }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  const toggleExpand = (source: string) => {
    if (expanded === source) setExpanded(null);
    else {
      setExpanded(source);
      if (!failures[source]) loadFailures(source);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>CheckUp</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            last refresh: {lastRefresh.toLocaleTimeString()}
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          <Button
            size="small"
            variant="outlined"
            onClick={async () => {
              try {
                const res = await apiFetch('/api/checkup/send-digest', { method: 'POST' });
                const json = await res.json();
                alert(json.sent
                  ? `Digest sent to: ${(json.recipients || []).join(', ')}`
                  : `Not sent: ${json.reason}`);
              } catch (e: any) {
                alert('Failed: ' + e.message);
              }
            }}
          >
            Send Test Digest
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Auto-refreshes every 60 seconds. Click a row to see the last 20 failed records for that source.
      </Typography>

      {data?.rapidApiGate && (
        <Alert severity={data.rapidApiGate.killed ? 'warning' : 'info'} sx={{ mb: 2 }}>
          RapidAPI Gate: {data.rapidApiGate.killed ? '🔒 KILLED' : '🟢 OPEN'}
          {data.rapidApiGate.dailyUsed !== null && data.rapidApiGate.dailyLimit !== null && (
            <> · Daily usage: {data.rapidApiGate.dailyUsed.toLocaleString()} / {data.rapidApiGate.dailyLimit.toLocaleString()}</>
          )}
        </Alert>
      )}

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, mt: 1 }}>
            Scrapers — Cycle Rotation
          </Typography>
          <Paper sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell><strong>Source</strong></TableCell>
                    <TableCell><strong>Mode</strong></TableCell>
                    <TableCell><strong>Cycle / Progress</strong></TableCell>
                    <TableCell><strong>Last Activity</strong></TableCell>
                    <TableCell><strong>Threshold</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.scrapers.map(row => (
                    <Fragment key={row.source}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => toggleExpand(row.source)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expanded === row.source
                              ? <KeyboardArrowDownIcon fontSize="small" />
                              : <KeyboardArrowRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell><strong>{row.source}</strong></TableCell>
                        <TableCell>{row.mode}</TableCell>
                        <TableCell>{row.detail}</TableCell>
                        <TableCell>{formatAge(row.heartbeatMinutesAgo)}</TableCell>
                        <TableCell>{row.thresholdMin ? `${row.thresholdMin}m` : '—'}</TableCell>
                        <TableCell>{scraperChip(row.status)}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expanded === row.source} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: '#f9fafb' }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Last 20 failed records (24h) — {row.source}
                              </Typography>
                              {failuresLoading === row.source ? (
                                <CircularProgress size={20} />
                              ) : failures[row.source]?.length ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><strong>Time</strong></TableCell>
                                      <TableCell><strong>Address</strong></TableCell>
                                      <TableCell><strong>Error</strong></TableCell>
                                      <TableCell><strong>Stage</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {failures[row.source].map(f => (
                                      <TableRow key={f._id}>
                                        <TableCell>{new Date(f.updatedAt).toLocaleString()}</TableCell>
                                        <TableCell>{f.fullAddress}</TableCell>
                                        <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {f.amvError}
                                        </TableCell>
                                        <TableCell>{f.dealStage}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No failures in the last 24 hours.
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            BofA Workers — Processing Status
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Red only when queue has records AND worker is idle, OR when proxy errors spike.
          </Typography>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Worker</strong></TableCell>
                    <TableCell align="right"><strong>Queue</strong></TableCell>
                    <TableCell><strong>Last Processed</strong></TableCell>
                    <TableCell align="right"><strong>Processed (1h)</strong></TableCell>
                    <TableCell align="right"><strong>Success % (24h)</strong></TableCell>
                    <TableCell align="right"><strong>Proxy % (24h)</strong></TableCell>
                    <TableCell align="right"><strong>no_data</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.bofaWorkers.map(row => {
                    const successLow  = row.successPct !== null && row.successPct < row.successRedPct;
                    const proxyHigh   = row.proxyPct >= row.proxyRedPct;
                    return (
                      <TableRow key={row.source}>
                        <TableCell>
                          <strong>{row.source}</strong>
                          {row.note && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {row.note}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{row.queueCount.toLocaleString()}</TableCell>
                        <TableCell>{formatAge(row.lastProcessedMinutesAgo)}</TableCell>
                        <TableCell align="right">{row.processedLast1h.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: successLow ? 'error.main' : 'inherit', fontWeight: successLow ? 700 : 400 }}>
                          {row.successPct === null ? '—' : `${row.successPct}%`}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            baseline ≥ {row.successRedPct}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: proxyHigh ? 'error.main' : 'inherit', fontWeight: proxyHigh ? 700 : 400 }}>
                          {row.proxyPct}%
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            baseline &lt; {row.proxyRedPct}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{row.errors24h.noData.toLocaleString()}</TableCell>
                        <TableCell>{bofaChip(row.status)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
